import { useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/common/ui/alert-dialog";
import { Label } from "@/common/ui/label";
import { Textarea } from "@/common/ui/textarea";

import type { PendingRequestView } from "../entitlements-api";
import { formatRequestAge } from "./request-age";
import { DENIAL_REASON_REQUIRED, normalizeDenialReason } from "./request-decision";
import { RequesterText } from "./RequesterText";

export interface RequestDenialDialogProps {
  readonly request: PendingRequestView;
  /** What was asked for, in the requester's own terms. */
  readonly label: string;
  readonly busy: boolean;
  readonly onConfirm: (reason: string) => void;
  readonly onCancel: () => void;
}

/**
 * The reason a denial requires, collected before the write rather than after.
 *
 * The field is not optional and the service agrees: `denyRequest` refuses an
 * empty reason before it touches the row. Collecting it here is what turns
 * that refusal into something the operator can act on, instead of a 400 that
 * arrives once they have already pressed Deny.
 *
 * The label says whose words these are. The reason is relayed to the requester
 * verbatim (ENR-1948), so an operator writing an internal note here would be
 * publishing it to a customer, and the one moment to say so is while they are
 * typing it.
 */
export function RequestDenialDialog({
  request,
  label,
  busy,
  onConfirm,
  onCancel,
}: RequestDenialDialogProps): React.ReactElement {
  const [reason, setReason] = useState("");
  const [attempted, setAttempted] = useState(false);
  /**
   * Whether this dialog's own denial has been sent. Local rather than read
   * from `busy`, which is the parent's state and does not commit until after
   * the click that started the write; dismissing in that window would take the
   * confirmation down over a write already in flight.
   */
  const [sent, setSent] = useState(false);
  const settling = busy || sent;
  const normalized = normalizeDenialReason(reason);

  return (
    <AlertDialog
      open
      onOpenChange={(open) => {
        // Dismissal only, and never while the write is in flight: Radix reports
        // Cancel, Escape and an outside click through this one callback, so
        // denying from here would deny when the operator declined.
        if (!open && !settling) onCancel();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle data-testid="request-denial-title">Deny this request?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p data-testid="request-denial-summary">
                {label}, asked for by{" "}
                <RequesterText request={request} testId={`requester-denial-${request.id}`} />{" "}
                <span className="tabular-nums">
                  {formatRequestAge(request.ageSeconds, request.createdAt)}
                </span>
                .
              </p>
              <p>
                Denying changes no entitlements. The request is closed, and the requester can file
                another.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2">
          <Label htmlFor="request-denial-reason" data-testid="request-denial-reason-label">
            Reason, shown to the requester exactly as written
          </Label>
          <Textarea
            id="request-denial-reason"
            data-testid="request-denial-reason"
            required
            rows={3}
            value={reason}
            disabled={settling}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Why this was not granted, and what would change the answer."
          />
          {attempted && normalized === null && (
            <p className="text-sm text-destructive" data-testid="request-denial-error">
              {DENIAL_REASON_REQUIRED}
            </p>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="request-denial-cancel" disabled={settling}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="request-denial-confirm"
            disabled={settling}
            onClick={(event) => {
              /* The dialog is unmounted by the caller once the write settles,
                 so Radix must not close it first and lose the in-flight label,
                 nor close it over a reason that was never written. */
              event.preventDefault();
              setAttempted(true);
              if (normalized === null) return;
              setSent(true);
              onConfirm(normalized);
            }}
          >
            {settling ? "Denying…" : "Deny"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
